import { caseConstants } from "@_constants/case.constants";
import { formatDatetime, formatRelativeDays } from "@_utils/helpers";

/**
 * Label + hover tooltip for the activity-card timestamp column.
 *
 * Scheduled emails show a relative day count. When paused, days are counted
 * from content.paused_at (frozen delay) instead of now. Failed/sent scheduled
 * emails keep the absolute scheduled_at — pause/resume does not change that.
 *
 * @param {object} activity
 * @param {Date|string} [now]
 * @returns {{ label: string, tooltip: string|null }}
 */
export function getActivityTimestampDisplay(activity, now = new Date()) {
  // Absolute datetime with no tooltip — used for non-scheduled activities and
  // for finalised (sent/failed) scheduled emails.
  const absolute = (value) => ({
    label: formatDatetime(value, "medium"),
    tooltip: null,
  });

  if (activity.type !== caseConstants.CASEACTIVITY_TYPE_EMAIL_SCHEDULED) {
    return absolute(activity.created);
  }

  const { scheduled_at, status, paused_at } = activity.content || {};

  // Pause/resume no longer applies once the email is sent or failed.
  if (
    status === caseConstants.CASE_ACTIVITY_STATE_FAILED ||
    status === caseConstants.CASE_ACTIVITY_STATE_SENT
  ) {
    return absolute(scheduled_at);
  }

  // Active/paused: relative day count. Pausing freezes the countdown at
  // paused_at instead of now.
  const isPaused = status === caseConstants.CASE_ACTIVITY_STATE_PAUSED;
  const from = isPaused && paused_at ? paused_at : now;

  return {
    label: formatRelativeDays(from, scheduled_at),
    tooltip: isPaused
      ? "after resuming the email automation"
      : formatDatetime(scheduled_at, "medium"),
  };
}
